// REQ-P1-10: ブラウザ標準の確認ダイアログ(window 標準API)を置き換える共通確認ダイアログ。
//
// 使い方:
//   const confirmDialog = useConfirm();
//   const ok = await confirmDialog({ message: 'このテンプレートを削除しますか？', danger: true });
//   if (!ok) return;
//
// 注: 関数名は confirmDialog とする(凍結ガードの検出パターンと
// 衝突しないため、destructure名を confirm にしないこと)。
//
// 方針:
// - 確認UXの正本はこのコンポーネント1つ。画面個別のダイアログ実装は追加しない。
// - danger: true は破壊的操作(削除・停止・無効化)用。確認ボタンが警告色になる。
// - a11y: role=dialog / aria-modal / ESCで取消 / 初期フォーカスは取消側 /
//   Tabは2ボタン間を循環 / 閉じたら呼び出し元へフォーカスを返す。

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import '../css/common/ConfirmDialog.css';

export interface ConfirmOptions {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 破壊的操作(削除・停止・無効化)の場合に true。確認ボタンが警告色になる。 */
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

const ConfirmContext = createContext<{ confirm: ConfirmFn } | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      openerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setPending({ options, resolve });
    });
  }, []);

  const settle = useCallback(
    (value: boolean) => {
      if (!pending) return;
      pending.resolve(value);
      setPending(null);
      // 呼び出し元のボタンへフォーカスを返す。
      openerRef.current?.focus?.();
      openerRef.current = null;
    },
    [pending]
  );

  useEffect(() => {
    if (!pending) return undefined;
    cancelButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        settle(false);
        return;
      }
      if (event.key === 'Tab') {
        // 2ボタン間の単純なフォーカス循環。
        event.preventDefault();
        const active = document.activeElement;
        if (active === cancelButtonRef.current) {
          confirmButtonRef.current?.focus();
        } else {
          cancelButtonRef.current?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [pending, settle]);

  const value = useMemo(() => ({ confirm }), [confirm]);
  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {options ? (
        <div
          className="confirm-dialog-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) settle(false);
          }}
        >
          <div
            className="confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-message"
          >
            <h2 id="confirm-dialog-title" className="confirm-dialog-title">
              {options.title ?? '確認'}
            </h2>
            <p id="confirm-dialog-message" className="confirm-dialog-message">
              {options.message}
            </p>
            <div className="confirm-dialog-actions">
              <button
                type="button"
                ref={cancelButtonRef}
                className="confirm-dialog-button confirm-dialog-cancel"
                onClick={() => settle(false)}
              >
                {options.cancelLabel ?? 'キャンセル'}
              </button>
              <button
                type="button"
                ref={confirmButtonRef}
                className={
                  options.danger
                    ? 'confirm-dialog-button confirm-dialog-danger'
                    : 'confirm-dialog-button confirm-dialog-primary'
                }
                onClick={() => settle(true)}
              >
                {options.confirmLabel ?? 'OK'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within <ConfirmProvider>');
  }
  return context.confirm;
}
